/**
 * E6. Уличные кости: с 14 лет, везде, без лицензии. Две честные кости против
 * «улицы»: сумма больше 7 — «выигрыш ×1» (ставка возвращается, в журнале
 * `minigame.dice.push`), ровно 7 — ×3, меньше 7 — ставка потеряна.
 * Ожидание = (15·0 + 6·2 − 15·1)/36 = −1/12 ≈ −8,3 % — ровно число спеки,
 * поэтому «улица» не мухлюет костями (формула плана «>7 → +bet» дала бы +39 %).
 * Без ставки (денег нет) кости не бросаются — `noPlay()`.
 *
 * Риски после броска:
 *  - страна с `homicide` выше медианы датасета: p = 5 % — ограбление
 *    (`minigame.dice.robbed`): деньги −ставка (всё, что было с собой),
 *    здоровье −10, запись тяжёлая;
 *  - illegal и возраст ≥ 18: p = 3 % — штраф/арест (`minigame.dice.arrest`):
 *    деньги −0,2 годового дохода сверх результата броска.
 * Тяга +2, отношения +1 (улица знакомит).
 */
import { gamblingLaw, getValue, type Dataset, type GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { clampBet, expectancy, noPlay, yearlyIncome } from './common';

export const DICE_MIN_AGE = 14;
export const DICE_SEVEN_PAYOUT = 3;
export const DICE_ROBBERY_P = 0.05;
export const DICE_ROBBERY_HEALTH = -10;
export const DICE_ARREST_P = 0.03;
export const DICE_ARREST_MIN_AGE = 18;
export const DICE_ARREST_INCOME_SHARE = 0.2;
export const DICE_URGE = 2;
/** Ожидаемый чистый результат на единицу ставки. */
export const DICE_EXPECTATION = (15 * 0 + 6 * (DICE_SEVEN_PAYOUT - 1) - 15 * 1) / 36;

export interface DiceState {
  maxBet: number;
  /** `homicide` страны выше медианы по датасету. */
  highCrime: boolean;
  /** Азарт в стране запрещён (штраф/арест с 18 лет). */
  illegal: boolean;
  /** Годовой доход для штрафа. */
  income: number;
}

export interface DiceInput {
  bet: number;
}

/** Медиана показателя по всем странам датасета в этом году (страны без данных пропускаются). */
export function medianIndicator(ds: Dataset, indicator: 'homicide', year: number): number {
  const values: number[] = [];
  for (const c of ds.countries) {
    try {
      values.push(getValue(ds, c.iso3, indicator, year).v);
    } catch {
      // нет ряда — страна не участвует в медиане
    }
  }
  if (values.length === 0) return 0;
  values.sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 1 ? (values[mid] as number) : ((values[mid - 1] as number) + (values[mid] as number)) / 2;
}

export function isHighCrime(ds: Dataset, life: Life): boolean {
  const year = life.birthYear + life.age;
  try {
    return getValue(ds, life.iso3, 'homicide', year).v > medianIndicator(ds, 'homicide', year);
  } catch {
    return false;
  }
}

export function rollDice(rng: Rng): [number, number] {
  return [rng.int(1, 6), rng.int(1, 6)];
}

export const dice: MinigameDef<DiceInput, DiceState> = {
  id: 'dice',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.age >= DICE_MIN_AGE;
  },
  setup(life: Life, ds: Dataset): DiceState {
    return {
      maxBet: Math.max(0, life.money),
      highCrime: isHighCrime(ds, life),
      illegal: gamblingLaw(life.iso3).law === 'illegal',
      income: yearlyIncome(ds, life),
    };
  },
  play(state: DiceState, input: DiceInput, life: Life, rng: Rng): MinigameOutcome {
    const bet = clampBet(Math.min(input.bet, state.maxBet), life.money);
    if (bet <= 0) return noPlay();
    const [a, b] = rollDice(rng);
    const sum = a + b;
    let moneyDelta = sum > 7 ? 0 : sum === 7 ? bet * (DICE_SEVEN_PAYOUT - 1) : -bet;
    let logKey = sum > 7 ? 'minigame.dice.push' : sum === 7 ? 'minigame.dice.win' : 'minigame.dice.lose';
    let heavy = false;
    const stats: MinigameOutcome['stats'] = { relations: 1 };
    const robbed = state.highCrime && rng.chance(DICE_ROBBERY_P);
    const arrested = !robbed && state.illegal && life.age >= DICE_ARREST_MIN_AGE && rng.chance(DICE_ARREST_P);
    if (robbed) {
      moneyDelta = -bet;
      stats.health = DICE_ROBBERY_HEALTH;
      logKey = 'minigame.dice.robbed';
      heavy = true;
    } else if (arrested) {
      moneyDelta -= DICE_ARREST_INCOME_SHARE * Math.max(0, state.income);
      logKey = 'minigame.dice.arrest';
    }
    const outcome: MinigameOutcome = {
      moneyDelta,
      stats,
      urgeDelta: DICE_URGE,
      logKey,
      logParams: { amount: Math.round(Math.abs(moneyDelta)), sum, a, b },
    };
    if (heavy) outcome.heavy = true;
    return outcome;
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: () => expectancy('dice', 1 + DICE_EXPECTATION),
};
